/**
 * Cache keyed con TTL + dedupe de llamadas en vuelo.
 *
 * Semántica:
 * - `get(key, fetcher)`: si hay valor fresco (< ttl) lo devuelve; si hay un
 *   fetch en vuelo para la misma key, comparte esa Promise (dedupe).
 * - Los errores NO se cachean: un fetch fallido libera la key para retry.
 * - `null`/`undefined` sí se cachean (respuesta válida, ej. perfil inexistente).
 * - `force: true` ignora el valor cacheado pero comparte in-flight si existe.
 * - `set` = write-through tras una escritura conocida.
 */

type Entry = {
  hasValue: boolean;
  value: unknown;
  fetchedAt: number;
  inFlight?: Promise<unknown>;
};

export type KeyedTtlCache = {
  get<T>(key: string, fetcher: () => Promise<T>, opts?: { force?: boolean }): Promise<T>;
  set(key: string, value: unknown): void;
  invalidate(key: string): void;
  clear(): void;
  /** Solo para tests/diagnóstico. */
  peek(key: string): { hasValue: boolean; fetchedAt: number };
};

export function createKeyedTtlCache(ttlMs: number): KeyedTtlCache {
  const entries = new Map<string, Entry>();

  function get<T>(key: string, fetcher: () => Promise<T>, opts?: { force?: boolean }): Promise<T> {
    const entry = entries.get(key);
    const fresh =
      !opts?.force &&
      entry?.hasValue === true &&
      Date.now() - entry.fetchedAt < ttlMs;

    if (fresh) return Promise.resolve(entry.value as T);
    if (entry?.inFlight) return entry.inFlight as Promise<T>;

    const e: Entry = { hasValue: false, value: undefined, fetchedAt: 0 };
    entries.set(key, e);
    e.inFlight = fetcher()
      .then((value) => {
        e.hasValue = true;
        e.value = value;
        e.fetchedAt = Date.now();
        return value;
      })
      .finally(() => {
        e.inFlight = undefined;
        // Si falló y nunca hubo valor, la key queda libre para retry.
        if (!e.hasValue) entries.delete(key);
      });
    return e.inFlight as Promise<T>;
  }

  return {
    get,
    set(key, value) {
      const e = entries.get(key) ?? { hasValue: false, value: undefined, fetchedAt: 0 };
      e.hasValue = true;
      e.value = value;
      e.fetchedAt = Date.now();
      entries.set(key, e);
    },
    invalidate(key) {
      entries.delete(key);
    },
    clear() {
      entries.clear();
    },
    peek(key) {
      const e = entries.get(key);
      return { hasValue: e?.hasValue === true, fetchedAt: e?.fetchedAt ?? 0 };
    },
  };
}
